import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import LoadingScreen from "../components/LoadingScreen";
import Card from "../components/Card";
import Button from "../components/Button";
import Tag from "../components/Tag";
import Table from "../components/Table";
import iconDownload from "../assets/icons/download.svg";
import "./styles.css";

export default function AutorizacionDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { permissions } = useAuth();
    const [autorizacion, setAutorizacion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingPdfFor, setDownloadingPdfFor] = useState(null);

    const handleDownloadPdf = async (userId) => {
        if (downloadingPdfFor) return;
        setDownloadingPdfFor(userId);
        try {
            const response = await api.get(
                `/autorizaciones/${id}/cazadores/${userId}/pdf`,
                { responseType: "blob" },
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `autorizacion-${id}-${userId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (err.response?.status !== 401) {
                Sentry.captureException(new Error("Error downloading PDF: " + err.message));
            }
            const msg = err.response?.status === 404
                ? "No se encontró el PDF para este cazador"
                : "Error al descargar el PDF";
            alert(msg);
        } finally {
            setDownloadingPdfFor(null);
        }
    };

    useEffect(() => {
        fetchAutorizacion();
    }, [id]);

    const fetchAutorizacion = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/autorizaciones/${id}`);
            setAutorizacion(response.data.autorizacion || response.data);
        } catch (err) {
            Sentry.captureException(new Error('Error loading autorizacion: ' + err.message));
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingScreen />;
    if (error) return <div className="error-message">Error: {error}</div>;
    if (!autorizacion) return <div>No se encontró la autorización</div>;

    // Columnas para la tabla de cazadores
    const cazadoresColumns = [
        // {
        //     key: "numero",
        //     label: "Nº",
        //     sortable: false,
        // },
        {
            key: "nombre_completo",
            label: "Nombre",
            sortable: false,
        },
        {
            key: "activacion",
            label: "Firmada",
            sortable: false,
        },
        ...(permissions?.canViewVisor
            ? [
                  {
                      key: "pdf",
                      label: "Autorización PDF",
                      sortable: false,
                      render: (_value, row) => (
                          <div style={{ display: "flex", justifyContent: "center" }}>
                              <Button
                                  variant="accent"
                                  size="s"
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadPdf(row.id);
                                  }}
                                  disabled={downloadingPdfFor === row.id}
                                  title="Descargar autorización en PDF"
                              >
                                  {downloadingPdfFor === row.id ? (
                                      "..."
                                  ) : (
                                      <img
                                          src={iconDownload}
                                          alt="PDF"
                                          className="button-icon button-icon-white"
                                      />
                                  )}
                              </Button>
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    // Columnas para la tabla de especies
    const especiesColumns = [
        {
            key: "numero",
            label: "Nº",
            sortable: false,
        },
        {
            key: "nombre_especie",
            label: "Especie",
            sortable: false,
        },
    ];

    // Columnas para la tabla de precintos
    const precintosColumns = [
        {
            key: "nr",
            label: "Nº Precinto",
            sortable: false,
        },
        {
            key: "estado",
            label: "Estado",
            sortable: false,
            render: (value) => {
                let variant = "default";
                if (value === "activo") variant = "success";
                if (value === "capturado") variant = "info";
                if (value === "inactivo") variant = "error";
                return <Tag variant={variant}>{value}</Tag>;
            },
        },
        {
            key: "especie",
            label: "Especie",
            sortable: false,
        },
        {
            key: "sexo",
            label: "Sexo",
            sortable: false,
        },
    ];

    return (
        <div className="page-especie-detalle">
            <div className="page-header-detalle">
                <div className="page-title-section">
                    <h1 className="page-title-detalle">
                        Autorización {autorizacion.tipo}
                    </h1>
                    <div className="breadcrumbs">
                        <button
                            className="breadcrumb-link"
                            onClick={() => navigate("/autorizaciones")}
                        >
                            Autorizaciones
                        </button>
                        <span className="breadcrumb-separator"> &gt; </span>
                        <span className="breadcrumb-current">
                            {autorizacion.tipo}
                        </span>
                    </div>
                </div>
            </div>

            <div className="page-content-detalle">
                <div className="page-grid-detalle">
                    <div className="page-grid-left-detalle">
                        <Card>
                            <div className="card-content">
                                <h3 className="card-title-detalle">
                                    Datos de la autorización
                                </h3>
                                <div className="capture-details">
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Coto
                                        </span>
                                        <span className="detail-value">
                                            <Tag variant="success" style={{ flex: "auto" }}>
                                                {autorizacion.nombre_coto} (
                                                {autorizacion.matricula_coto})
                                            </Tag>
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Titular/Gestor
                                        </span>
                                        <span className="detail-value">
                                            {autorizacion.nombre_titular}
                                        </span>
                                    </div>
                                    {autorizacion.modalidades && (
                                        <div className="detail-row">
                                            <span className="detail-label">
                                                Alias
                                            </span>
                                            <span className="detail-value">
                                                {autorizacion.modalidades}
                                            </span>
                                        </div>
                                    )}
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Tipo
                                        </span>
                                        <span className="detail-value">
                                            {autorizacion.tipo}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Formato
                                        </span>
                                        <span className="detail-value">
                                            <Tag
                                                variant={
                                                    autorizacion.formato ===
                                                    "app"
                                                        ? "success"
                                                        : "info"
                                                }
                                            >
                                                {autorizacion.formato === "app"
                                                    ? "APP (Capturcyl)"
                                                    : "En papel"}
                                            </Tag>
                                        </span>
                                    </div>

                                    {autorizacion.modalidades && (
                                        <div className="detail-row">
                                            <span className="detail-label">
                                                Modalidades
                                            </span>
                                            <span className="detail-value">
                                                {autorizacion.modalidades}
                                            </span>
                                        </div>
                                    )}
                                    {autorizacion.emision && (
                                        <div className="detail-row">
                                            <span className="detail-label">
                                                Emisión
                                            </span>
                                            <span className="detail-value">
                                                {autorizacion.emision}
                                            </span>
                                        </div>
                                    )}
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Fecha desde
                                        </span>
                                        <span className="detail-value">
                                            {autorizacion.desde}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Fecha hasta
                                        </span>
                                        <span className="detail-value">
                                            {autorizacion.hasta}
                                        </span>
                                    </div>
                                    {autorizacion.estado !== "anulada" &&
                                        autorizacion.estado !== "publicada" &&
                                        autorizacion.estado !== "eliminada" && (
                                            <div className="detail-row">
                                                <span className="detail-label">
                                                    Celebrada
                                                </span>
                                                <span className="detail-value">
                                                    <Tag
                                                        variant={
                                                            autorizacion.no_celebrada == true
                                                                ? "error"
                                                                : "success"
                                                        }
                                                    >
                                                        {autorizacion.no_celebrada ? "No" : "Si"}
                                                    </Tag>
                                                </span>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </Card>

                        {autorizacion.especies &&
                            autorizacion.especies.length > 0 && (
                                <Card style={{ marginTop: "24px" }}>
                                    <div className="card-content">
                                        <h3 className="card-title-detalle">
                                            Especies autorizadas (
                                            {autorizacion.especies.length})
                                        </h3>
                                        <div
                                            style={{
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                border: "1px solid var(--neutral-200)",
                                            }}
                                        >
                                            <Table
                                                columns={especiesColumns}
                                                data={autorizacion.especies.map(
                                                    (especie, index) => ({
                                                        ...especie,
                                                        numero: index + 1,
                                                    }),
                                                )}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            )}
                    </div>

                    <div className="page-grid-right-detalle">
                        {autorizacion.cazadores &&
                            autorizacion.cazadores.length > 0 && (
                                <Card>
                                    <div className="card-content">
                                        <h3 className="card-title-detalle">
                                            Cazadores autorizados (
                                            {autorizacion.cazadores.length})
                                        </h3>
                                        <div
                                            style={{
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                border: "1px solid var(--neutral-200)",
                                            }}
                                        >
                                            <Table
                                                columns={cazadoresColumns}
                                                data={autorizacion.cazadores.map(
                                                    (cazador, index) => ({
                                                        ...cazador,
                                                        numero: index + 1,
                                                    }),
                                                )}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            )}

                        {autorizacion.precintos &&
                            autorizacion.precintos.length > 0 && (
                                <Card style={{ marginTop: "24px" }}>
                                    <div className="card-content">
                                        <h3 className="card-title-detalle">
                                            Precintos asignados (
                                            {autorizacion.precintos.length})
                                        </h3>
                                        <div
                                            style={{
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                border: "1px solid var(--neutral-200)",
                                            }}
                                        >
                                            <Table
                                                columns={precintosColumns}
                                                data={autorizacion.precintos}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
}


